import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { questionnaireService, QuestionnaireVersion, QuestionnaireWithQuestions } from '../services/questionnaireService';
import { dimensionService } from '../services/dimensionService';
import { useAuth } from '../contexts/AuthContext';
import { useHeader } from '../contexts/HeaderContext';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  Bars3Icon,
  ListBulletIcon,
  DocumentTextIcon,
  Squares2X2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  ArchiveBoxIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import QuestionEditor from '../components/questionnaire/QuestionEditor';
import {
  calculateWeightPercentage,
  calculateRawWeightFromPercentage,
  getQuestionRawWeight,
} from '../utils/weightCalculations';
import { Question } from '../types';

/**
 * Questionnaire Template Editor
 * 
 * This page is for creating and managing Questionnaire Templates (definitions).
 * These templates define which questions are used when creating assessments.
 * 
 * Note: This is NOT for viewing filled-out assessment questionnaires.
 * For that, see the Assessment detail page or the Questionnaire page for a specific assessment.
 */
const QuestionnaireEditor: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { setTitle } = useHeader();

  useEffect(() => {
    setTitle('Questionnaire Templates');
  }, [setTitle]);

  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<number | null>(null); // Changed to numeric ID
  const [selectedVersion, setSelectedVersion] = useState<number | undefined>(undefined);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSeedModal, setShowSeedModal] = useState(false);
  const [showImportCsvModal, setShowImportCsvModal] = useState(false);
  const [deleteVersionsModalGroup, setDeleteVersionsModalGroup] = useState<QuestionnaireVersion | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showQuestionEditor, setShowQuestionEditor] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState<string | undefined>(undefined);
  const [questionsOrder, setQuestionsOrder] = useState<string[]>([]);
  const [collapsedDimensions, setCollapsedDimensions] = useState<Set<number>>(new Set());
  const [localWeightPercentages, setLocalWeightPercentages] = useState<Map<string, number>>(new Map());
  const [dimensionOrder, setDimensionOrder] = useState<number[]>([]);
  const [editingQuestionnaireName, setEditingQuestionnaireName] = useState<boolean>(false);
  const [editingQuestionnaireDescription, setEditingQuestionnaireDescription] = useState<boolean>(false);
  const [localQuestionnaireName, setLocalQuestionnaireName] = useState<string>('');
  const [localQuestionnaireDescription, setLocalQuestionnaireDescription] = useState<string>('');
  const [showAddQuestionMenu, setShowAddQuestionMenu] = useState<string | null>(null); // questionId or 'new' for empty state

  // Check if user is super admin or admin
  // Use custom claims permissions instead of role checks
  const hasQuestionnairesRead = user?.permissions?.includes('questionnaires:read') || user?.permissions?.includes('admin:full');
  const hasQuestionnairesWrite = user?.permissions?.includes('questionnaires:write') || user?.permissions?.includes('admin:full');

  const { data: questionnaires, isLoading } = useQuery(
    'questionnaires',
    () => questionnaireService.getQuestionnaires(),
    {
      enabled: hasQuestionnairesRead,
    }
  );

  // Fetch dimensions to get proper names
  const { data: dimensions } = useQuery(
    'dimensions',
    () => dimensionService.getDimensions(),
    {
      enabled: hasQuestionnairesRead,
    }
  );

  const { data: questionnaire } = useQuery(
    ['questionnaire', selectedQuestionnaireId, selectedVersion],
    () => {
      if (!selectedQuestionnaireId) return null;
      // Use numeric ID only - no label lookup
      return questionnaireService.getQuestionnaire(selectedQuestionnaireId, selectedVersion, false);
    },
    {
      enabled: !!selectedQuestionnaireId && hasQuestionnairesRead,
    }
  );

  const createMutation = useMutation(
    (data: { questionnaireId: string; name: string; description?: string }) =>
      questionnaireService.createQuestionnaire(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('questionnaires', { refetchActive: true, refetchInactive: true });
        toast.success('Questionnaire created successfully!');
        setShowCreateModal(false);
      },
      onError: (err: any) => {
        toast.error(err.message || 'Failed to create questionnaire');
      },
    }
  );

  const duplicateMutation = useMutation(
    (data: { questionnaireId: number; newQuestionnaireId: string; name: string; description?: string }) =>
      questionnaireService.duplicateQuestionnaire(data.questionnaireId, {
        newQuestionnaireId: data.newQuestionnaireId,
        name: data.name,
        description: data.description,
      }, false), // Use numeric ID, not label
    {
      onSuccess: () => {
        queryClient.invalidateQueries('questionnaires', { refetchActive: true, refetchInactive: true });
        toast.success('Questionnaire duplicated successfully!');
        setShowDuplicateModal(false);
      },
      onError: (err: any) => {
        toast.error(err.message || 'Failed to duplicate questionnaire');
      },
    }
  );

  const archiveTemplateMutation = useMutation(
    (label: string) => questionnaireService.archiveQuestionnaireTemplate(label),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('questionnaires', { refetchActive: true, refetchInactive: true });
        queryClient.invalidateQueries(['questionnaire', selectedQuestionnaireId], { refetchActive: true, refetchInactive: true });
        toast.success('Template archived');
      },
      onError: (err: any) => {
        toast.error(err?.message || 'Failed to archive template');
      },
    }
  );

  const deleteTemplateVersionsMutation = useMutation(
    async ({
      templateNumericId,
      versions,
    }: {
      templateNumericId: number;
      versions: number[];
    }) => {
      for (const v of versions) {
        await questionnaireService.deleteVersion(templateNumericId, v, false);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('questionnaires', { refetchActive: true, refetchInactive: true });
        setSelectedQuestionnaireId(null);
        setDeleteVersionsModalGroup(null);
        toast.success('Selected version(s) deleted');
      },
      onError: (err: any) => {
        toast.error(err?.message || 'Failed to delete version(s)');
      },
    }
  );

  const createFromSeedMutation = useMutation(
    (data: { newQuestionnaireId: string; name: string; description?: string }) =>
      questionnaireService.createQuestionnaireFromSeed(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('questionnaires', { refetchActive: true, refetchInactive: true });
        toast.success('Template created from seed questionnaire');
        setShowSeedModal(false);
      },
      onError: (err: any) => {
        toast.error(err?.message || 'Failed to create from seed');
      },
    }
  );

  const importCsvMutation = useMutation(
    (data: { file: File; newQuestionnaireId: string; name: string; description?: string }) =>
      questionnaireService.importQuestionnaireFromCsv(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('questionnaires', { refetchActive: true, refetchInactive: true });
        toast.success('Template imported from CSV');
        setShowImportCsvModal(false);
      },
      onError: (err: any) => {
        toast.error(err?.message || 'Failed to import CSV');
      },
    }
  );

  const confirmArchiveTemplate = (label: string) => {
    if (
      !window.confirm(
        `Archive template "${label}"? It will no longer be available for new evaluations (existing evaluations are unchanged).`
      )
    ) {
      return;
    }
    archiveTemplateMutation.mutate(label);
  };

  const handleExportCsv = async (q: QuestionnaireVersion) => {
    try {
      const active = q.versions.find((v) => v.isActive);
      const rowId = active?.id ?? q.id;
      if (rowId == null || Number.isNaN(Number(rowId))) {
        toast.error('Could not resolve questionnaire id for export');
        return;
      }
      const blob = await questionnaireService.exportQuestionnaireCsv(Number(rowId));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = String(q.questionnaireId ?? q.label ?? 'template').replace(/[^\w.-]+/g, '_');
      a.download = `${safe}-questionnaire.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (e: any) {
      toast.error(e?.message || 'Export failed');
    }
  };

  const activateVersionMutation = useMutation(
    ({ questionnaireId, version }: { questionnaireId: number; version: number }) =>
      questionnaireService.activateVersion(questionnaireId, version, false), // Use numeric ID, not label
    {
      onSuccess: () => {
        queryClient.invalidateQueries('questionnaires', { refetchActive: true, refetchInactive: true });
        queryClient.invalidateQueries(['questionnaire', selectedQuestionnaireId], { refetchActive: true, refetchInactive: true });
        toast.success('Version activated successfully!');
        setShowVersionModal(false);
      },
      onError: (err: any) => {
        toast.error(err.message || 'Failed to activate version');
      },
    }
  );

  const reorderQuestionsMutation = useMutation(
    ({ questionnaireId, dimensions }: { questionnaireId: number; dimensions: Array<{ dimensionId: number; questionIds: string[] }> }) =>
      questionnaireService.reorderQuestions(questionnaireId, { dimensions }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['questionnaire', selectedQuestionnaireId], { refetchActive: true, refetchInactive: true });
        toast.success('Questions reordered successfully!');
      },
      onError: (err: any) => {
        toast.error(err.message || 'Failed to reorder questions');
      },
    }
  );

  const reorderDimensionsMutation = useMutation(
    (orderedIds: number[]) => dimensionService.reorderDimensions(orderedIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('dimensions', { refetchActive: true, refetchInactive: true });
        queryClient.invalidateQueries(['questionnaire', selectedQuestionnaireId], { refetchActive: true, refetchInactive: true });
        toast.success('Dimension order updated');
      },
      onError: (err: any) => {
        toast.error(err.message || 'Failed to reorder dimensions');
      },
    }
  );

  const updateWeightMutation = useMutation(
    ({ questionId, weight }: { questionId: string; weight: number }) => {
      if (!selectedQuestionnaireId) throw new Error('No questionnaire selected');
      // Find the question to get its unique id (questionId can be label or legacy questionId)
      const question = questionnaire?.questions.find(q => (q.label || q.questionId) === questionId);
      if (!question) throw new Error('Question not found');
      // Use the unique id for the update
      return questionnaireService.updateQuestion(selectedQuestionnaireId, question.id, {
        weighting: weight
      });
    },
    {
      onSuccess: () => {
        // Invalidate to refetch questionnaire data
        queryClient.invalidateQueries(['questionnaire', selectedQuestionnaireId], { refetchActive: true, refetchInactive: true });
        toast.success('Weight updated');
      },
      onError: (err: any) => {
        toast.error(err.message || 'Failed to update weight');
      },
    }
  );

  // Simple weight update: convert percentage to float and update ONE question
  // All percentages will recalculate automatically after refresh
  const updateQuestionWeight = (
    questionId: string,
    percentage: number,
    peerQuestions: QuestionnaireWithQuestions['questions'],
    _context: 'group' | 'dimension'
  ) => {
    if (!questionnaire?.questions) return;

    const question = questionnaire.questions.find(q => (q.label || q.questionId) === questionId);
    if (!question) return;

    // Get current raw weight (float)
    const currentRawWeight = getQuestionRawWeight(question);

    // Convert peer questions to QuestionForWeight format
    const peerQuestionsForWeight = peerQuestions.map(pq => ({
      questionId: pq.label || pq.questionId || String(pq.id),
      weighting: getQuestionRawWeight(pq)
    }));

    // Calculate new raw weight (float) from desired percentage
    const newRawWeight = calculateRawWeightFromPercentage(
      percentage,
      currentRawWeight,
      peerQuestionsForWeight
    );

    // Update ONLY this question's weight (float) in the database
    // After refresh, all percentages will recalculate automatically
    updateWeightMutation.mutate({ questionId, weight: newRawWeight });
  };


  // Get color for weight percentage slider based on value (0 to 100)
  const getWeightPercentageColor = (percentage: number): string => {
    // Normalize percentage to 0-1 range
    const normalized = percentage / 100;

    // Color gradient: red (low) -> yellow (middle) -> green (high)
    if (normalized < 0.5) {
      // Red to Yellow (0 to 0.5)
      const ratio = normalized * 2; // 0 to 1
      const r = Math.round(255);
      const g = Math.round(255 * ratio);
      const b = Math.round(0);
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      // Yellow to Green (0.5 to 1)
      const ratio = (normalized - 0.5) * 2; // 0 to 1
      const r = Math.round(255 * (1 - ratio));
      const g = Math.round(255);
      const b = Math.round(0);
      return `rgb(${r}, ${g}, ${b})`;
    }
  };

  // Sync local questionnaire name and description when questionnaire changes
  useEffect(() => {
    if (questionnaire) {
      if (!editingQuestionnaireName) {
        setLocalQuestionnaireName(questionnaire.name);
      }
      if (!editingQuestionnaireDescription) {
        setLocalQuestionnaireDescription(questionnaire.description || '');
      }
    }
  }, [questionnaire?.name, questionnaire?.description, editingQuestionnaireName, editingQuestionnaireDescription]);

  // Close dropdown menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showAddQuestionMenu && !(event.target as Element).closest('.add-question-menu-container')) {
        setShowAddQuestionMenu(null);
      }
    };

    if (showAddQuestionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showAddQuestionMenu]);

  // Calculate weight percentages per dimension when questionnaire data changes
  useEffect(() => {
    if (questionnaire?.questions) {
      setQuestionsOrder(questionnaire.questions.map(q => q.label || q.questionId || ''));

      // Calculate weight percentages per dimension
      const newLocalPercentages = new Map<string, number>();
      const questionsByDimension = new Map<number, typeof questionnaire.questions>();

      // Group questions by dimension
      questionnaire.questions.forEach(q => {
        const dimId = q.dimensionId || 0;
        if (!questionsByDimension.has(dimId)) {
          questionsByDimension.set(dimId, []);
        }
        questionsByDimension.get(dimId)!.push(q);
      });

      // Calculate percentages for each dimension
      questionsByDimension.forEach((dimQuestions) => {
        // Identify parent-child relationships
        dimQuestions.forEach(q => {
          const qIdentifier = q.label || q.questionId || String(q.id);
          const isChild = q.childBranches?.some((b: any) => b.isActive !== false) ||
            dimQuestions.some(potentialParent =>
              potentialParent.parentBranches?.some((pb: any) => {
                const pbChildLabel = pb.childQuestion?.label || pb.childQuestionLabel;
                return (pb.childQuestionId === q.id || pbChildLabel === qIdentifier) && pb.isActive !== false;
              })
            );

          const isParent = q.parentBranches?.some((b: any) => b.isActive !== false) ||
            dimQuestions.some(potentialChild =>
              potentialChild.childBranches?.some((cb: any) => {
                const cbParentLabel = cb.parentQuestion?.label || cb.parentQuestionLabel;
                return (cb.parentQuestionId === q.id || cbParentLabel === qIdentifier) && cb.isActive !== false;
              })
            );

          // Calculate group weight percentage for parent/child questions
          if (isChild || isParent) {
            let groupPeerQuestions: typeof dimQuestions = [];

            if (isChild) {
              // Find parent
              let parentId: string | undefined;
              const activeChildBranch = q.childBranches?.find((b: any) => b.isActive !== false);
              parentId = activeChildBranch?.parentQuestion?.label || activeChildBranch?.parentQuestionLabel;
              if (!parentId) {
                const parentQ = dimQuestions.find(pq =>
                  pq.parentBranches?.some((pb: any) => {
                    const pbChildLabel = pb.childQuestion?.label || pb.childQuestionLabel;
                    return (pb.childQuestionId === q.id || pbChildLabel === qIdentifier) && pb.isActive !== false;
                  })
                );
                parentId = parentQ ? (parentQ.label || parentQ.questionId || String(parentQ.id)) : undefined;
              }

              if (parentId) {
                const qIdentifier = q.label || q.questionId || String(q.id);
                const parentQ = dimQuestions.find(pq => {
                  const pqIdentifier = pq.label || pq.questionId || String(pq.id);
                  return pqIdentifier === parentId;
                });
                const siblings = dimQuestions.filter(pq => {
                  const pqIdentifier = pq.label || pq.questionId || String(pq.id);
                  return pqIdentifier !== qIdentifier &&
                    (pq.childBranches?.some((cb: any) => {
                      const cbParentLabel = cb.parentQuestion?.label || cb.parentQuestionLabel;
                      return (cb.parentQuestionId === parentQ?.id || cbParentLabel === parentId) && cb.isActive !== false;
                    }) ||
                      parentQ?.parentBranches?.some((pb: any) => {
                        const pbChildLabel = pb.childQuestion?.label || pb.childQuestionLabel;
                        return (pb.childQuestionId === pq.id || pbChildLabel === pqIdentifier) && pb.isActive !== false;
                      }));
                });
                groupPeerQuestions = [q, ...siblings];
                if (parentQ) groupPeerQuestions.push(parentQ);
              }
            } else if (isParent) {
              // Find children
              const children = dimQuestions.filter(pq => {
                const pqIdentifier = pq.label || pq.questionId || String(pq.id);
                return pq.childBranches?.some((cb: any) => {
                  const cbParentLabel = cb.parentQuestion?.label || cb.parentQuestionLabel;
                  return (cb.parentQuestionId === q.id || cbParentLabel === qIdentifier) && cb.isActive !== false;
                }) ||
                  q.parentBranches?.some((pb: any) => {
                    const pbChildLabel = pb.childQuestion?.label || pb.childQuestionLabel;
                    return (pb.childQuestionId === pq.id || pbChildLabel === pqIdentifier) && pb.isActive !== false;
                  });
              });
              groupPeerQuestions = [q, ...children];
            }

            if (groupPeerQuestions.length > 0) {
              const groupPercentage = calculateWeightPercentage(
                getQuestionRawWeight(q),
                groupPeerQuestions.map((pq) => ({
                  questionId: pq.label || pq.questionId || String(pq.id),
                  weighting: getQuestionRawWeight(pq),
                }))
              );
              newLocalPercentages.set(qIdentifier, groupPercentage);
            }
          }

          // Calculate dimension weight percentage for non-child questions
          if (!isChild) {
            const topLevelQuestions = dimQuestions.filter(pq => {
              const hasChildLink = pq.childBranches?.some((b: any) => b.isActive !== false);
              if (hasChildLink) return false;
              const claimedByParent = dimQuestions.some(potentialParent =>
                potentialParent.parentBranches?.some((pb: any) => pb.childQuestionId === pq.id && pb.isActive !== false)
              );
              return !claimedByParent;
            });

            if (topLevelQuestions.length > 0) {
              const dimensionPercentage = calculateWeightPercentage(
                getQuestionRawWeight(q),
                topLevelQuestions.map((pq) => ({
                  questionId: pq.label || pq.questionId || String(pq.id),
                  weighting: getQuestionRawWeight(pq),
                }))
              );
              newLocalPercentages.set(`${qIdentifier}_dimension`, dimensionPercentage);
            }
          }
        });
      });

      setLocalWeightPercentages(newLocalPercentages);
    }
  }, [questionnaire?.questions]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !selectedQuestionnaireId) return;

    // Check if dragging a dimension (dimension-level drag)
    if (result.type === 'DIMENSION') {
      const sourceIndex = result.source.index;
      const destIndex = result.destination.index;

      // Reorder dimensions
      const newOrder = Array.from(dimensionOrder);
      const [movedDimension] = newOrder.splice(sourceIndex, 1);
      newOrder.splice(destIndex, 0, movedDimension);

      setDimensionOrder(newOrder);
      reorderDimensionsMutation.mutate(newOrder);
      return;
    }

    // Question-level drag - only allow reordering within the same dimension
    const sourceDroppableId = result.source.droppableId;
    const destDroppableId = result.destination.droppableId;

    // Prevent moving questions between dimensions via drag and drop
    if (sourceDroppableId !== destDroppableId) {
      toast.error('Questions cannot be moved between dimensions via drag and drop. Use the dimension dropdown in the question editor to change dimensions.');
      return;
    }

    // Only allow reordering within the same dimension
    if (sourceDroppableId === destDroppableId && sourceDroppableId.startsWith('dimension-')) {
      const dimId = parseInt(sourceDroppableId.replace('dimension-', ''));

      // Get current questions for this dimension
      const dimQuestions = questionsByDimension[dimId];
      if (!dimQuestions) return;

      // Create array of IDs for this dimension
      const dimQuestionIds = dimQuestions.map(q => q.label || q.questionId || String(q.id));

      // Reorder LOCAL list
      const [movedId] = dimQuestionIds.splice(result.source.index, 1);
      dimQuestionIds.splice(result.destination.index, 0, movedId);

      // Reconstruct global list by flattening all dimensions in current order
      // keeping the modified dimension's new order
      const newGlobalOrder: string[] = [];
      const dimensionsPayload: Array<{ dimensionId: number; questionIds: string[] }> = [];
      orderedDimensions.forEach(dId => {
        const qIds = dId === dimId
          ? dimQuestionIds
          : (questionsByDimension[dId] || []).map(q => q.label || q.questionId || String(q.id));
        newGlobalOrder.push(...qIds);
        dimensionsPayload.push({
          dimensionId: dId,
          questionIds: qIds,
        });
      });

      setQuestionsOrder(newGlobalOrder);
      reorderQuestionsMutation.mutate({
        questionnaireId: selectedQuestionnaireId,
        dimensions: dimensionsPayload,
      });
    }
  };

  // Get sorted questions based on order
  // IMPORTANT: Always include ALL questions from questionnaire, not just those in questionsOrder
  // This ensures child questions and newly added questions are always visible
  const sortedQuestions = useMemo(() => {
    if (!questionnaire?.questions) return [];

    // Get all questionIds from the questionnaire
    const allQuestionIds = questionnaire.questions.map((q) => q.label || q.questionId || String(q.id));

    // If we have a custom order, use it but merge with any missing questions
    if (questionsOrder.length > 0) {
      // Start with questions in the custom order
      const orderedIds = [...questionsOrder];

      // Add any questions that exist in questionnaire but aren't in the order
      allQuestionIds.forEach((id) => {
        if (!orderedIds.includes(id)) {
          orderedIds.push(id);
        }
      });

      // Map to actual questions, preserving order
      return orderedIds
        .map(id => questionnaire.questions.find(q => (q.label || q.questionId) === id))
        .filter(Boolean) as typeof questionnaire.questions;
    }

    // No custom order - use all questions sorted by displayOrder or natural order
    return [...questionnaire.questions].sort((a, b) => {
      // Try to sort by displayOrder if available
      const aOrder = a.displayOrder ?? 999;
      const bOrder = b.displayOrder ?? 999;
      if (aOrder !== bOrder) return aOrder - bOrder;
      // Fallback to label for consistent ordering (renamed from questionId)
      const aLabel = a.label || a.questionId || '';
      const bLabel = b.label || b.questionId || '';
      return aLabel.localeCompare(bLabel);
    });
  }, [questionnaire?.questions, questionsOrder]);

  // Group questions by dimension, ordered by dimensionOrder
  const questionsByDimension = useMemo(() => {
    const grouped: Record<number, typeof sortedQuestions> = {};
    sortedQuestions.forEach(q => {
      const dimId = q.dimensionId || 0;
      if (!grouped[dimId]) {
        grouped[dimId] = [];
      }
      grouped[dimId].push(q);
    });
    return grouped;
  }, [sortedQuestions]);

  // Get ordered dimensions - use displayOrder from question.dimension if available, otherwise from dimensions API
  const orderedDimensions = useMemo(() => {
    // If we have a custom drag-and-drop order, prioritize it
    if (dimensionOrder.length > 0) {
      // Return local state order, filtering for those that actually have questions or exist
      return dimensionOrder.filter(id => questionsByDimension[id]?.length > 0);
    }

    // Extract unique dimensions from questions with their displayOrder
    const dimensionMap = new Map<number, { id: number; displayOrder: number }>();

    sortedQuestions.forEach(q => {
      if (q.dimensionId && q.dimension) {
        const displayOrder = (q.dimension as any).displayOrder ?? 999;
        if (!dimensionMap.has(q.dimensionId) || dimensionMap.get(q.dimensionId)!.displayOrder > displayOrder) {
          dimensionMap.set(q.dimensionId, {
            id: q.dimensionId,
            displayOrder: displayOrder
          });
        }
      }
    });

    // If we have dimensions from questions, use those
    if (dimensionMap.size > 0) {
      return Array.from(dimensionMap.values())
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map(d => d.id)
        .filter(dimId => questionsByDimension[dimId]?.length > 0);
    }

    // Fallback to dimensions API if available
    if (dimensions && dimensions.length > 0) {
      const sortedDimensions = [...dimensions].sort((a, b) => a.displayOrder - b.displayOrder);
      return sortedDimensions
        .map(d => d.id)
        .filter(dimId => questionsByDimension[dimId]?.length > 0);
    }

    // Final fallback to natural order
    return Object.keys(questionsByDimension).map(Number).sort((a, b) => a - b);
  }, [sortedQuestions, dimensions, questionsByDimension, dimensionOrder]);

  // Get dimension name helper - use fetched dimensions for proper names
  const getDimensionName = (dimensionId: number) => {
    if (dimensions && Array.isArray(dimensions)) {
      const dimension = dimensions.find((d) => d.id === dimensionId);
      if (dimension) return dimension.label || dimension.name || `Dimension ${dimensionId}`;
    }
    // Fallback to question's dimension if available
    const question = sortedQuestions.find(q => q.dimensionId === dimensionId);
    return question?.dimension?.label || question?.dimension?.name || `Dimension ${dimensionId}`;
  };

  // Toggle dimension collapse
  const toggleDimensionCollapse = (dimensionId: number) => {
    setCollapsedDimensions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dimensionId)) {
        newSet.delete(dimensionId);
      } else {
        newSet.add(dimensionId);
      }
      return newSet;
    });
  };

  // Initialize dimension order when dimensions load
  useEffect(() => {
    if (dimensions) {
      // Dimensions are already sorted by displayOrder from the API
      const orderedIds = dimensions.map((d: any) => d.id);
      setDimensionOrder(orderedIds);
    }
  }, [dimensions]);

  // Questionnaire name and description editing
  const updateQuestionnaireMutation = useMutation(
    async ({ name, description }: { name?: string; description?: string }) => {
      if (!selectedQuestionnaireId) throw new Error('No questionnaire selected');
      return await questionnaireService.updateQuestionnaire(selectedQuestionnaireId, { name, description });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['questionnaire', selectedQuestionnaireId], { refetchActive: true, refetchInactive: true });
        setEditingQuestionnaireName(false);
        setEditingQuestionnaireDescription(false);
        toast.success('Questionnaire updated');
      },
      onError: (err: any) => {
        toast.error(err.message || 'Failed to update questionnaire');
      },
    }
  );

  const handleQuestionnaireNameEdit = () => {
    if (questionnaire) {
      setLocalQuestionnaireName(questionnaire.name);
      setEditingQuestionnaireName(true);
    }
  };

  const handleQuestionnaireNameSave = () => {
    if (localQuestionnaireName.trim()) {
      updateQuestionnaireMutation.mutate({ name: localQuestionnaireName.trim() });
    } else {
      setEditingQuestionnaireName(false);
    }
  };

  const handleQuestionnaireNameCancel = () => {
    setEditingQuestionnaireName(false);
    if (questionnaire) {
      setLocalQuestionnaireName(questionnaire.name);
    }
  };

  const handleQuestionnaireDescriptionEdit = () => {
    if (questionnaire) {
      setLocalQuestionnaireDescription(questionnaire.description || '');
      setEditingQuestionnaireDescription(true);
    }
  };

  const handleQuestionnaireDescriptionSave = () => {
    updateQuestionnaireMutation.mutate({ description: localQuestionnaireDescription.trim() || undefined });
  };

  const handleQuestionnaireDescriptionCancel = () => {
    setEditingQuestionnaireDescription(false);
    if (questionnaire) {
      setLocalQuestionnaireDescription(questionnaire.description || '');
    }
  };

  // Get question type icon
  const getQuestionTypeIcon = (type: string) => {
    switch (type) {
      case 'binary':
        return <CheckCircleIcon className="h-5 w-5 text-success-600" />;
      case 'scale':
        return <Bars3Icon className="h-5 w-5 text-primary-600" />;
      case 'multiple_choice':
        return <ListBulletIcon className="h-5 w-5 text-accent-600" />;
      case 'multiselect':
        return <Squares2X2Icon className="h-5 w-5 text-warning-600" />;
      case 'text':
        return <DocumentTextIcon className="h-5 w-5 text-neutral-600" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-neutral-400" />;
    }
  };

  if (!hasQuestionnairesRead) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Super admin access required to edit questionnaires.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Questionnaire Template Editor</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage questionnaire templates that define which questions are used in assessments
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasQuestionnairesWrite && (
            <>
              <button
                type="button"
                onClick={() => setShowSeedModal(true)}
                className="btn btn-secondary flex items-center gap-2"
              >
                <DocumentTextIcon className="h-5 w-5" />
                Create from seed template
              </button>
              <button
                type="button"
                onClick={() => setShowImportCsvModal(true)}
                className="btn btn-secondary flex items-center gap-2"
              >
                <ArrowUpTrayIcon className="h-5 w-5" />
                Import CSV
              </button>
            </>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            Create New Questionnaire
          </button>
        </div>
      </div>

      {/* Questionnaires List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Questionnaire Templates</h2>
          <p className="text-sm text-gray-500 mt-1">
            Create and manage questionnaire templates (definitions) that are used when creating assessments.
          </p>
        </div>
        <div className="divide-y divide-gray-200">
          {questionnaires && questionnaires.length > 0 ? (
            questionnaires.map((q: QuestionnaireVersion) => (
              <div key={q.id || q.questionnaireId} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-medium text-gray-900">
                        {q.versions.find((v) => v.isActive)?.name || q.questionnaireId}
                      </h3>
                      {q.isArchived && (
                        <span className="text-xs font-medium rounded-full bg-amber-100 text-amber-900 px-2 py-0.5">
                          Archived
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 font-mono mt-0.5">{q.questionnaireId}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {q.versions.length} version{q.versions.length !== 1 ? 's' : ''}
                      {' • '}
                      Active: v{q.versions.find(v => v.isActive)?.version || 'N/A'}
                      {' • '}
                      {q.totalAssessmentCount ??
                        q.versions.reduce((sum, v) => sum + (v.assessmentCount ?? 0), 0)}{' '}
                      evaluation
                      {(q.totalAssessmentCount ??
                        q.versions.reduce((sum, v) => sum + (v.assessmentCount ?? 0), 0)) !== 1
                        ? 's'
                        : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedQuestionnaireId(q.id); // Use numeric ID
                        setSelectedVersion(undefined);
                      }}
                      className="btn btn-secondary flex items-center gap-1 text-sm"
                    >
                      <PencilIcon className="h-4 w-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setShowDuplicateModal(true);
                        setSelectedQuestionnaireId(q.id); // Use numeric ID
                      }}
                      className="btn btn-secondary flex items-center gap-1 text-sm"
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" />
                      Duplicate
                    </button>
                    <button
                      onClick={() => {
                        setShowVersionModal(true);
                        setSelectedQuestionnaireId(q.id); // Use numeric ID
                      }}
                      className="btn btn-secondary flex items-center gap-1 text-sm"
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                      Versions
                    </button>
                    {hasQuestionnairesWrite && (
                      <button
                        type="button"
                        onClick={() => confirmArchiveTemplate(q.questionnaireId)}
                        className="btn btn-secondary flex items-center gap-1 text-sm"
                        disabled={archiveTemplateMutation.isLoading || !!q.isArchived}
                        title={q.isArchived ? 'Already archived' : 'Archive template'}
                      >
                        <ArchiveBoxIcon className="h-4 w-4" />
                        Archive
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleExportCsv(q)}
                      className="btn btn-secondary flex items-center gap-1 text-sm"
                      title="Download this template as CSV"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      Export CSV
                    </button>
                    {hasQuestionnairesWrite && (
                      <button
                        type="button"
                        onClick={() => setDeleteVersionsModalGroup(q)}
                        className="btn btn-secondary flex items-center gap-1 text-sm text-red-700 border-red-200 hover:bg-red-50"
                        disabled={deleteTemplateVersionsMutation.isLoading}
                      >
                        <TrashIcon className="h-4 w-4" />
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-8 text-center text-gray-500">
              <p className="text-base font-medium text-gray-900 mb-2">No questionnaire templates found</p>
              <p className="text-sm text-gray-600 mb-4">
                Questionnaire templates define the questions used in assessments. If you have existing questions without a template,
                you can migrate them to create a default questionnaire.
              </p>
              <p className="text-sm text-gray-500">
                Create your first questionnaire template to get started.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Selected Questionnaire Details */}
      {selectedQuestionnaireId && questionnaire && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                {editingQuestionnaireName ? (
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={localQuestionnaireName}
                      onChange={(e) => setLocalQuestionnaireName(e.target.value)}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-lg font-medium"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleQuestionnaireNameSave();
                        } else if (e.key === 'Escape') {
                          handleQuestionnaireNameCancel();
                        }
                      }}
                      disabled={updateQuestionnaireMutation.isLoading}
                    />
                    <button
                      onClick={handleQuestionnaireNameSave}
                      className="text-green-600 hover:text-green-800 disabled:opacity-50"
                      disabled={updateQuestionnaireMutation.isLoading}
                    >
                      <CheckCircleIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={handleQuestionnaireNameCancel}
                      className="text-red-600 hover:text-red-800"
                      disabled={updateQuestionnaireMutation.isLoading}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mb-2">
                    <h2 className="text-lg font-medium text-gray-900">{questionnaire.name}</h2>
                    {hasQuestionnairesWrite && (
                      <button
                        onClick={handleQuestionnaireNameEdit}
                        className="text-gray-400 hover:text-gray-600"
                        title="Edit questionnaire name"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
                <p className="text-sm text-gray-500">
                  Version {questionnaire.version} • {questionnaire.questions.length} questions
                  {questionnaire.assessmentCount !== undefined && (
                    <> • {questionnaire.assessmentCount} assessment{questionnaire.assessmentCount !== 1 ? 's' : ''}</>
                  )}
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedQuestionnaireId(null);
                  setSelectedVersion(undefined);
                }}
                className="btn btn-secondary text-sm"
              >
                Close
              </button>
            </div>
          </div>
          <div className="px-6 py-4">
            {editingQuestionnaireDescription ? (
              <div className="mb-4">
                <div className="flex items-start gap-2">
                  <textarea
                    value={localQuestionnaireDescription}
                    onChange={(e) => setLocalQuestionnaireDescription(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 min-h-[80px]"
                    placeholder="Enter description..."
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        handleQuestionnaireDescriptionCancel();
                      }
                    }}
                    disabled={updateQuestionnaireMutation.isLoading}
                  />
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={handleQuestionnaireDescriptionSave}
                      className="text-green-600 hover:text-green-800 disabled:opacity-50"
                      disabled={updateQuestionnaireMutation.isLoading}
                      title="Save description"
                    >
                      <CheckCircleIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={handleQuestionnaireDescriptionCancel}
                      className="text-red-600 hover:text-red-800"
                      disabled={updateQuestionnaireMutation.isLoading}
                      title="Cancel editing"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 mb-4">
                <p className="text-sm text-gray-700 flex-1">
                  {questionnaire.description || <span className="text-gray-400 italic">No description</span>}
                </p>
                {hasQuestionnairesWrite && (
                  <button
                    onClick={handleQuestionnaireDescriptionEdit}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                    title="Edit description"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">Questions</h3>
                {hasQuestionnairesWrite && sortedQuestions.length > 0 && (
                  <div className="text-xs text-gray-500">
                    <p>Drag dimensions to reorder • Drag questions to reorder within dimension</p>
                    <p className="text-gray-400 italic">To move questions between dimensions, use the dimension dropdown in the question editor</p>
                  </div>
                )}
              </div>
              {sortedQuestions.length > 0 ? (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="dimensions-list" type="DIMENSION">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-6">
                        {orderedDimensions.map((dimId, dimIndex) => {
                          const questions = questionsByDimension[dimId] || [];
                          if (questions.length === 0) return null;

                          const isCollapsed = collapsedDimensions.has(dimId);
                          const dimensionName = getDimensionName(dimId);

                          return (
                            <Draggable
                              key={`dimension-${dimId}`}
                              draggableId={`dimension-${dimId}`}
                              index={dimIndex}
                              isDragDisabled={!hasQuestionnairesWrite}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`border border-gray-200 rounded-lg p-4 bg-gray-50 ${snapshot.isDragging ? 'shadow-lg bg-blue-50' : ''
                                    }`}
                                >
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2 flex-1">
                                      {hasQuestionnairesWrite && (
                                        <div
                                          {...provided.dragHandleProps}
                                          className="text-gray-400 hover:text-gray-600 cursor-move"
                                          title="Drag to reorder dimension"
                                        >
                                          <Bars3Icon className="h-5 w-5" />
                                        </div>
                                      )}
                                      <button
                                        onClick={() => toggleDimensionCollapse(dimId)}
                                        className="text-gray-400 hover:text-gray-600"
                                      >
                                        {isCollapsed ? (
                                          <ChevronDownIcon className="h-5 w-5" />
                                        ) : (
                                          <ChevronUpIcon className="h-5 w-5" />
                                        )}
                                      </button>
                                      <h4 className="text-sm font-semibold text-gray-900">
                                        {dimensionName}
                                      </h4>
                                    </div>
                                    <span className="text-xs text-gray-500">{questions.length} question{questions.length !== 1 ? 's' : ''}</span>
                                  </div>
                                  {!isCollapsed && (
                                    <Droppable droppableId={`dimension-${dimId}`} type="QUESTION">
                                      {(provided, snapshot) => (
                                        <div
                                          {...provided.droppableProps}
                                          ref={provided.innerRef}
                                          className={`space-y-2 min-h-[50px] ${snapshot.isDraggingOver ? 'bg-blue-50 rounded p-2' : ''}`}
                                        >
                                          {questions.map((q, index) => {
                                            // Determine question relationships
                                            const isChild = q.childBranches?.some((b: any) => b.isActive !== false) ||
                                              questions.some(potentialParent =>
                                                potentialParent.parentBranches?.some((pb: any) => 
                                                  (pb.childQuestionId === q.id || (pb.childQuestion?.label || pb.childQuestionLabel) === (q.label || q.questionId)) && pb.isActive !== false
                                                )
                                              );

                                            const isParent = q.parentBranches?.some((b: any) => b.isActive !== false) ||
                                              questions.some(potentialChild =>
                                                potentialChild.childBranches?.some((cb: any) => 
                                                  (cb.parentQuestionId === q.id || (cb.parentQuestion?.label || cb.parentQuestionLabel) === (q.label || q.questionId)) && cb.isActive !== false
                                                )
                                              );

                                            // Find parent question if this is a child
                                            let parentId: string | undefined;
                                            if (isChild) {
                                              const qIdentifier = q.label || q.questionId || String(q.id);
                                              const activeChildBranch = q.childBranches?.find((b: any) => b.isActive !== false);
                                              // Try to get parent from nested question object first, then fallback to label field
                                              parentId = activeChildBranch?.parentQuestion?.label || activeChildBranch?.parentQuestionLabel;
                                              if (!parentId) {
                                                const parentQ = questions.find(pq =>
                                                  pq.parentBranches?.some((pb: any) => {
                                                    const pbChildLabel = pb.childQuestion?.label || pb.childQuestionLabel;
                                                    return (pb.childQuestionId === q.id || pbChildLabel === qIdentifier) && pb.isActive !== false;
                                                  })
                                                );
                                                parentId = parentQ ? (parentQ.label || parentQ.questionId || String(parentQ.id)) : undefined;
                                              }
                                            }

                                            // Calculate nesting level for indentation (0 = top level, 1 = child, 2 = grandchild, etc.)
                                            let nestingLevel = 0;
                                            if (isChild && parentId) {
                                              // For now, assume single level nesting. Deep nesting would require recursive calculation
                                              nestingLevel = 1;
                                            }
                                            const indentPx = nestingLevel * 32; // 2rem = 32px per level

                                            // Calculate group peer questions (parent + all children in the group)
                                            let groupPeerQuestions: typeof questions = [];
                                            if (isChild && parentId) {
                                              // Child: group = parent + all siblings + self
                                              const qIdentifier = q.label || q.questionId || String(q.id);
                                              const parentQ = questions.find(pq => {
                                                const pqIdentifier = pq.label || pq.questionId || String(pq.id);
                                                return pqIdentifier === parentId;
                                              });
                                              const siblings = questions.filter(pq => {
                                                const pqIdentifier = pq.label || pq.questionId || String(pq.id);
                                                return pqIdentifier !== qIdentifier &&
                                                  (pq.childBranches?.some((cb: any) => {
                                                    const cbParentLabel = cb.parentQuestion?.label || cb.parentQuestionLabel;
                                                    return (cb.parentQuestionId === parentQ?.id || cbParentLabel === parentId) && cb.isActive !== false;
                                                  }) ||
                                                    parentQ?.parentBranches?.some((pb: any) => {
                                                      const pbChildLabel = pb.childQuestion?.label || pb.childQuestionLabel;
                                                      return (pb.childQuestionId === pq.id || pbChildLabel === pqIdentifier) && pb.isActive !== false;
                                                    }));
                                              });
                                              groupPeerQuestions = [q, ...siblings];
                                              if (parentQ) groupPeerQuestions.push(parentQ);
                                            } else if (isParent) {
                                              // Parent: group = self + all children
                                              const qIdentifier = q.label || q.questionId || String(q.id);
                                              const children = questions.filter(pq => {
                                                const pqIdentifier = pq.label || pq.questionId || String(pq.id);
                                                return pq.childBranches?.some((cb: any) => {
                                                  const cbParentLabel = cb.parentQuestion?.label || cb.parentQuestionLabel;
                                                  return (cb.parentQuestionId === q.id || cbParentLabel === qIdentifier) && cb.isActive !== false;
                                                }) ||
                                                  q.parentBranches?.some((pb: any) => {
                                                    const pbChildLabel = pb.childQuestion?.label || pb.childQuestionLabel;
                                                    return (pb.childQuestionId === pq.id || pbChildLabel === pqIdentifier) && pb.isActive !== false;
                                                  });
                                              });
                                              groupPeerQuestions = [q, ...children];
                                            }

                                            // Calculate dimension-level peer questions (only top-level, non-child questions)
                                            const topLevelQuestions = questions.filter(pq => {
                                              const hasChildLink = pq.childBranches?.some((b: any) => b.isActive !== false);
                                              if (hasChildLink) return false;
                                              const claimedByParent = questions.some(potentialParent =>
                                                potentialParent.parentBranches?.some((pb: any) => pb.childQuestionId === pq.id && pb.isActive !== false)
                                              );
                                              return !claimedByParent;
                                            });

                                            // Calculate percentages
                                            let groupPercentage = 0;
                                            let dimensionPercentage = 0;

                                            if (isChild || isParent) {
                                              // Group weight percentage
                                              groupPercentage = calculateWeightPercentage(
                                                getQuestionRawWeight(q),
                                                groupPeerQuestions.map((pq) => ({
                                                  questionId: pq.label || pq.questionId || String(pq.id),
                                                  weighting: getQuestionRawWeight(pq),
                                                }))
                                              );
                                            }

                                            if (!isChild) {
                                              // Dimension weight percentage (only for non-child questions)
                                              dimensionPercentage = calculateWeightPercentage(
                                                getQuestionRawWeight(q),
                                                topLevelQuestions.map((pq) => ({
                                                  questionId: pq.label || pq.questionId || String(pq.id),
                                                  weighting: getQuestionRawWeight(pq),
                                                }))
                                              );
                                            }

                                            const questionIdentifier = q.label || q.questionId || String(q.id);
                                            const localGroupPercentage = localWeightPercentages.get(questionIdentifier) ?? groupPercentage;
                                            const localDimensionPercentage = localWeightPercentages.get(`${questionIdentifier}_dimension`) ?? dimensionPercentage;

                                            return (
                                              <Draggable key={q.label || q.questionId || q.id} draggableId={String(q.label || q.questionId || q.id)} index={index} isDragDisabled={!hasQuestionnairesWrite}>
                                                {(provided, snapshot) => (
                                                  <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    style={{
                                                      ...provided.draggableProps.style,
                                                      marginLeft: `${indentPx}px`,
                                                    }}
                                                    className={`border border-gray-200 rounded-lg p-3 bg-white transition-all ${snapshot.isDragging ? 'bg-blue-50 shadow-md' : ''
                                                      } ${isChild ? 'border-l-4 border-l-blue-400 bg-blue-50/30' : ''}`}
                                                  >
                                                    <div className="flex items-start justify-between">
                                                      <div className="flex-1">
                                                        {/* Child Question Indicator */}
                                                        {isChild && (
                                                          <div className="text-xs text-blue-500 font-medium mb-1 flex items-center gap-1">
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                                            </svg>
                                                            Child Question
                                                            {parentId && (() => {
                                                              const p = questions.find(pq => {
                                                                const pqIdentifier = pq.label || pq.questionId || String(pq.id);
                                                                return pqIdentifier === parentId;
                                                              });
                                                              return p ? (
                                                                <span className="text-gray-400 font-normal">
                                                                  • Parent: {p.questionText.substring(0, 30)}...
                                                                </span>
                                                              ) : null;
                                                            })()}
                                                          </div>
                                                        )}

                                                        <div className="flex items-center gap-2">
                                                          {hasQuestionnairesWrite && (
                                                            <div
                                                              {...provided.dragHandleProps}
                                                              className="cursor-move text-gray-400 hover:text-gray-600"
                                                            >
                                                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                                              </svg>
                                                            </div>
                                                          )}
                                                          {getQuestionTypeIcon(q.questionType)}
                                                          <span className="text-xs font-mono text-gray-500">{q.questionId}</span>
                                                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                                                            {q.questionType}
                                                          </span>
                                                          {isParent && (
                                                            <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded flex items-center gap-1">
                                                              <Squares2X2Icon className="w-3 h-3" />
                                                              Group Parent
                                                            </span>
                                                          )}
                                                        </div>
                                                        <p className="text-sm text-gray-900 mt-1">{q.questionText}</p>

                                                        {/* Scoring/Weighting Row */}
                                                        <div className="mt-2 pt-2 border-t border-gray-100 space-y-2">
                                                          {/* Group Weight Slider (for parent/child groups) */}
                                                          {(isChild || isParent) && (
                                                            <div className="flex items-center justify-between gap-4">
                                                              <div className="flex-1 flex items-center gap-3">
                                                                <span className="text-xs text-gray-500 whitespace-nowrap">Group Weight %:</span>
                                                                <div className="flex-1 relative">
                                                                  {/* Default weight indicator */}
                                                                  {(() => {
                                                                    const defaultGroupPercentage = groupPeerQuestions.length > 0 ? (100 / groupPeerQuestions.length) : 0;
                                                                    return (
                                                                      <div
                                                                        className="absolute top-0 bottom-0 w-0.5 bg-blue-400 opacity-60 pointer-events-none z-10"
                                                                        style={{
                                                                          left: `${defaultGroupPercentage}%`,
                                                                          transform: 'translateX(-50%)',
                                                                        }}
                                                                        title={`Default: ${defaultGroupPercentage.toFixed(1)}% (equal weight for ${groupPeerQuestions.length} group question${groupPeerQuestions.length !== 1 ? 's' : ''})`}
                                                                      />
                                                                    );
                                                                  })()}
                                                                  <input
                                                                    type="range"
                                                                    min="0"
                                                                    max="100"
                                                                    step="0.5"
                                                                    value={localGroupPercentage}
                                                                    onChange={(e) => {
                                                                      const val = parseFloat(e.target.value);
                                                                      const questionIdentifier = q.label || q.questionId || String(q.id);
                                                                      const newMap = new Map(localWeightPercentages);
                                                                      newMap.set(questionIdentifier, val);
                                                                      setLocalWeightPercentages(newMap);
                                                                    }}
                                                                    onMouseUp={(e) => {
                                                                      const val = parseFloat((e.target as HTMLInputElement).value);
                                                                      const questionIdentifier = q.label || q.questionId || String(q.id);
                                                                      updateQuestionWeight(questionIdentifier, val, groupPeerQuestions, 'group');
                                                                    }}
                                                                    onTouchEnd={(e) => {
                                                                      const val = parseFloat((e.target as HTMLInputElement).value);
                                                                      const questionIdentifier = q.label || q.questionId || String(q.id);
                                                                      updateQuestionWeight(questionIdentifier, val, groupPeerQuestions, 'group');
                                                                    }}
                                                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed relative z-0"
                                                                    disabled={!hasQuestionnairesWrite || updateWeightMutation.isLoading}
                                                                    style={{
                                                                      background: `linear-gradient(to right, 
                                                            ${getWeightPercentageColor(localGroupPercentage)} 0%, 
                                                            ${getWeightPercentageColor(localGroupPercentage)} ${localGroupPercentage}%, 
                                                            #e5e7eb ${localGroupPercentage}%, 
                                                            #e5e7eb 100%)`
                                                                    }}
                                                                  />
                                                                </div>
                                                                <div className="flex items-center gap-2 min-w-[100px]">
                                                                  <span className="text-xs font-mono text-gray-700 w-16 text-right">
                                                                    {localGroupPercentage.toFixed(1)}%
                                                                  </span>
                                                                  {groupPeerQuestions.length > 0 && (
                                                                    <span className="text-xs text-gray-400" title={`Default equal weight: ${(100 / groupPeerQuestions.length).toFixed(1)}%`}>
                                                                      (def: {(100 / groupPeerQuestions.length).toFixed(1)}%)
                                                                    </span>
                                                                  )}
                                                                </div>
                                                              </div>
                                                            </div>
                                                          )}

                                                          {/* Dimension Weight Slider (for parent questions and standalone questions) */}
                                                          {!isChild && (
                                                            <div className="flex items-center justify-between gap-4">
                                                              <div className="flex-1 flex items-center gap-3">
                                                                <span className="text-xs text-gray-500 whitespace-nowrap">Dimension Weight %:</span>
                                                                <div className="flex-1 relative">
                                                                  {/* Default weight indicator */}
                                                                  {(() => {
                                                                    const defaultDimensionPercentage = topLevelQuestions.length > 0 ? (100 / topLevelQuestions.length) : 0;
                                                                    return (
                                                                      <div
                                                                        className="absolute top-0 bottom-0 w-0.5 bg-blue-400 opacity-60 pointer-events-none z-10"
                                                                        style={{
                                                                          left: `${defaultDimensionPercentage}%`,
                                                                          transform: 'translateX(-50%)',
                                                                        }}
                                                                        title={`Default: ${defaultDimensionPercentage.toFixed(1)}% (equal weight for ${topLevelQuestions.length} top-level question${topLevelQuestions.length !== 1 ? 's' : ''})`}
                                                                      />
                                                                    );
                                                                  })()}
                                                                  <input
                                                                    type="range"
                                                                    min="0"
                                                                    max="100"
                                                                    step="0.5"
                                                                    value={localDimensionPercentage}
                                                                    onChange={(e) => {
                                                                      const val = parseFloat(e.target.value);
                                                                      const questionIdentifier = q.label || q.questionId || String(q.id);
                                                                      const newMap = new Map(localWeightPercentages);
                                                                      newMap.set(`${questionIdentifier}_dimension`, val);
                                                                      setLocalWeightPercentages(newMap);
                                                                    }}
                                                                    onMouseUp={(e) => {
                                                                      const val = parseFloat((e.target as HTMLInputElement).value);
                                                                      const questionIdentifier = q.label || q.questionId || String(q.id);
                                                                      updateQuestionWeight(questionIdentifier, val, topLevelQuestions, 'dimension');
                                                                    }}
                                                                    onTouchEnd={(e) => {
                                                                      const val = parseFloat((e.target as HTMLInputElement).value);
                                                                      const questionIdentifier = q.label || q.questionId || String(q.id);
                                                                      updateQuestionWeight(questionIdentifier, val, topLevelQuestions, 'dimension');
                                                                    }}
                                                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed relative z-0"
                                                                    disabled={!hasQuestionnairesWrite || updateWeightMutation.isLoading}
                                                                    style={{
                                                                      background: `linear-gradient(to right, 
                                                            ${getWeightPercentageColor(localDimensionPercentage)} 0%, 
                                                            ${getWeightPercentageColor(localDimensionPercentage)} ${localDimensionPercentage}%, 
                                                            #e5e7eb ${localDimensionPercentage}%, 
                                                            #e5e7eb 100%)`
                                                                    }}
                                                                  />
                                                                </div>
                                                                <div className="flex items-center gap-2 min-w-[100px]">
                                                                  <span className="text-xs font-mono text-gray-700 w-16 text-right">
                                                                    {localDimensionPercentage.toFixed(1)}%
                                                                  </span>
                                                                  {topLevelQuestions.length > 0 && (
                                                                    <span className="text-xs text-gray-400" title={`Default equal weight: ${(100 / topLevelQuestions.length).toFixed(1)}%`}>
                                                                      (def: {(100 / topLevelQuestions.length).toFixed(1)}%)
                                                                    </span>
                                                                  )}
                                                                </div>
                                                              </div>
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                      <div className="flex gap-2">
                                                        <button
                                                          className="btn btn-secondary text-xs"
                                                          onClick={() => {
                                                            const questionIdentifier = q.label || q.questionId || String(q.id);
                                                            setEditingQuestionId(questionIdentifier);
                                                            setShowQuestionEditor(true);
                                                          }}
                                                        >
                                                          Edit
                                                        </button>
                                                        {hasQuestionnairesWrite && (
                                                          <div className="relative add-question-menu-container">
                                                            <button
                                                              className="btn btn-secondary text-xs flex items-center gap-1"
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                const questionIdentifier = q.label || q.questionId || String(q.id);
                                                                setShowAddQuestionMenu(showAddQuestionMenu === questionIdentifier ? null : questionIdentifier);
                                                              }}
                                                            >
                                                              <PlusIcon className="h-4 w-4" />
                                                            </button>
                                                            {showAddQuestionMenu === (q.label || q.questionId || String(q.id)) && (
                                                              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-[180px] add-question-menu-container">
                                                                <button
                                                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                                                  onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setShowAddQuestionMenu(null);
                                                                    // Pre-select dimension and set display order for "above"
                                                                    const questionIdentifier = q.label || q.questionId || String(q.id);
                                                                    const currentIndex = questions.findIndex(pq => {
                                                                      const pqIdentifier = pq.label || pq.questionId || String(pq.id);
                                                                      return pqIdentifier === questionIdentifier;
                                                                    });
                                                                    const newDisplayOrder =
                                                                      currentIndex > 0
                                                                        ? questions[currentIndex - 1]?.displayOrder ?? 0
                                                                        : (q.displayOrder ?? 0) - 1;
                                                                    setEditingQuestionId(undefined);
                                                                    // Store context for question editor
                                                                    (window as any).__addQuestionContext = {
                                                                      dimensionId: q.dimensionId,
                                                                      displayOrder: newDisplayOrder,
                                                                      position: 'above',
                                                                      relativeToQuestionId: questionIdentifier,
                                                                    };
                                                                    setShowQuestionEditor(true);
                                                                  }}
                                                                >
                                                                  <PlusIcon className="h-4 w-4" />
                                                                  Add question above
                                                                </button>
                                                                <button
                                                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                                                  onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setShowAddQuestionMenu(null);
                                                                    // Pre-select dimension and set display order for "below"
                                                                    const questionIdentifier = q.label || q.questionId || String(q.id);
                                                                    const currentIndex = questions.findIndex(pq => {
                                                                      const pqIdentifier = pq.label || pq.questionId || String(pq.id);
                                                                      return pqIdentifier === questionIdentifier;
                                                                    });
                                                                    // Calculate display order: use the next question's order, or current + 1
                                                                    let newDisplayOrder = (q.displayOrder || 0) + 1;
                                                                    if (currentIndex < questions.length - 1) {
                                                                      const nextQuestion = questions[currentIndex + 1];
                                                                      if (nextQuestion.displayOrder !== undefined && nextQuestion.displayOrder !== null) {
                                                                        newDisplayOrder = nextQuestion.displayOrder;
                                                                      }
                                                                    }
                                                                    setEditingQuestionId(undefined);
                                                                    setShowAddQuestionMenu(null);
                                                                    (window as any).__addQuestionContext = {
                                                                      dimensionId: q.dimensionId,
                                                                      displayOrder: newDisplayOrder,
                                                                      position: 'below',
                                                                      relativeToQuestionId: questionIdentifier,
                                                                    };
                                                                    setShowQuestionEditor(true);
                                                                  }}
                                                                >
                                                                  <PlusIcon className="h-4 w-4" />
                                                                  Add question below
                                                                </button>
                                                                <button
                                                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 border-t border-gray-200"
                                                                  onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setShowAddQuestionMenu(null);
                                                                    // For child question, we'll handle this in the question editor
                                                                    const questionIdentifier = q.label || q.questionId || String(q.id);
                                                                    setEditingQuestionId(questionIdentifier);
                                                                    setShowAddQuestionMenu(null);
                                                                    (window as any).__addQuestionContext = {
                                                                      dimensionId: q.dimensionId,
                                                                      isChild: true,
                                                                      parentQuestionId: questionIdentifier,
                                                                    };
                                                                    setShowQuestionEditor(true);
                                                                  }}
                                                                >
                                                                  <Squares2X2Icon className="h-4 w-4" />
                                                                  Add child question
                                                                </button>
                                                              </div>
                                                            )}
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                )}
                                              </Draggable>
                                            );
                                          })}
                                          {provided.placeholder}
                                        </div>
                                      )}
                                    </Droppable>
                                  )}
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                  <p className="text-sm text-gray-500 mb-4">No questions in this questionnaire yet.</p>
                  {hasQuestionnairesWrite && (
                    <button
                      onClick={() => {
                        setEditingQuestionId(undefined);
                        setShowQuestionEditor(true);
                      }}
                      className="btn btn-primary text-sm"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Add First Question
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateQuestionnaireModal
          onClose={() => setShowCreateModal(false)}
          onCreate={createMutation.mutate}
          isLoading={createMutation.isLoading}
        />
      )}

      {showSeedModal && (
        <CreateFromSeedModal
          onClose={() => setShowSeedModal(false)}
          onCreate={(data) => createFromSeedMutation.mutate(data)}
          isLoading={createFromSeedMutation.isLoading}
        />
      )}

      {showImportCsvModal && (
        <ImportCsvModal
          onClose={() => setShowImportCsvModal(false)}
          onImport={(data) => importCsvMutation.mutate(data)}
          isLoading={importCsvMutation.isLoading}
        />
      )}

      {deleteVersionsModalGroup && (
        <DeleteTemplateVersionsModal
          template={deleteVersionsModalGroup}
          onClose={() => setDeleteVersionsModalGroup(null)}
          onDeleteSelected={(versions) =>
            deleteTemplateVersionsMutation.mutate({
              templateNumericId: deleteVersionsModalGroup.id,
              versions,
            })
          }
          isLoading={deleteTemplateVersionsMutation.isLoading}
        />
      )}

      {/* Duplicate Modal */}
      {showDuplicateModal && selectedQuestionnaireId && (
        <DuplicateQuestionnaireModal
          questionnaireId={selectedQuestionnaireId}
          onClose={() => {
            setShowDuplicateModal(false);
            setSelectedQuestionnaireId(null);
          }}
          onDuplicate={duplicateMutation.mutate}
          isLoading={duplicateMutation.isLoading}
        />
      )}

      {/* Version Modal */}
      {showVersionModal && selectedQuestionnaireId && (
        <VersionModal
          questionnaireId={selectedQuestionnaireId}
          onClose={() => {
            setShowVersionModal(false);
            setSelectedQuestionnaireId(null);
          }}
          onActivate={activateVersionMutation.mutate}
          isLoading={activateVersionMutation.isLoading}
        />
      )}

      {/* Question Editor Modal */}
      {showQuestionEditor && selectedQuestionnaireId && (
        <QuestionEditor
          questionId={editingQuestionId}
          questionnaireId={String(selectedQuestionnaireId)}
          question={
            editingQuestionId
              ? (questionnaire?.questions.find(
                  (q) => (q.label || q.questionId || String(q.id)) === editingQuestionId
                ) as Question | undefined)
              : undefined
          }
          onClose={() => {
            setShowQuestionEditor(false);
            setEditingQuestionId(undefined);
          }}
          onSave={() => {
            queryClient.invalidateQueries(['questionnaire', selectedQuestionnaireId]);
          }}
        />
      )}
    </div>
  );
};

// Create Questionnaire Modal Component
const CreateQuestionnaireModal: React.FC<{
  onClose: () => void;
  onCreate: (data: { questionnaireId: string; name: string; description?: string }) => void;
  isLoading: boolean;
}> = ({ onClose, onCreate, isLoading }) => {
  const [formData, setFormData] = useState({
    questionnaireId: '',
    name: '',
    description: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.questionnaireId || !formData.name) {
      toast.error('Questionnaire ID and name are required');
      return;
    }
    onCreate({
      questionnaireId: formData.questionnaireId,
      name: formData.name,
      description: formData.description || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Questionnaire</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Questionnaire ID *
            </label>
            <input
              type="text"
              value={formData.questionnaireId}
              onChange={(e) => setFormData({ ...formData, questionnaireId: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., default-v1"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., Default Maturity Assessment"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              rows={3}
              placeholder="Optional description"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreateFromSeedModal: React.FC<{
  onClose: () => void;
  onCreate: (data: { newQuestionnaireId: string; name: string; description?: string }) => void;
  isLoading: boolean;
}> = ({ onClose, onCreate, isLoading }) => {
  const [formData, setFormData] = useState({
    questionnaireId: '',
    name: '',
    description: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.questionnaireId || !formData.name) {
      toast.error('Template ID and name are required');
      return;
    }
    onCreate({
      newQuestionnaireId: formData.questionnaireId,
      name: formData.name,
      description: formData.description || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Create from seed template</h2>
        <p className="text-sm text-gray-600 mb-4">
          Builds a new template from the canonical seed definition (same source as the database seed script). This does not
          require the <span className="font-mono">default-emm-questionnaire</span> row to exist in the database.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New template ID (label) *
            </label>
            <input
              type="text"
              value={formData.questionnaireId}
              onChange={(e) => setFormData({ ...formData, questionnaireId: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., acme-maturity-2026"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., ACME Maturity Assessment"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              rows={3}
              placeholder="Optional description"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create from seed'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ImportCsvModal: React.FC<{
  onClose: () => void;
  onImport: (data: { file: File; newQuestionnaireId: string; name: string; description?: string }) => void;
  isLoading: boolean;
}> = ({ onClose, onImport, isLoading }) => {
  const [formData, setFormData] = useState({
    questionnaireId: '',
    name: '',
    description: '',
    file: null as File | null,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.file) {
      toast.error('Choose a CSV file');
      return;
    }
    if (!formData.questionnaireId || !formData.name) {
      toast.error('Template ID and name are required');
      return;
    }
    onImport({
      file: formData.file,
      newQuestionnaireId: formData.questionnaireId,
      name: formData.name,
      description: formData.description || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Import template from CSV</h2>
        <p className="text-sm text-gray-600 mb-4">
          Upload a CSV produced by <strong>Export CSV</strong>. A new template label is required and must not already exist.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CSV file *</label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const f = e.target.files?.[0];
                setFormData((prev) => ({ ...prev, file: f ?? null }));
              }}
              className="w-full text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New template ID (label) *
            </label>
            <input
              type="text"
              value={formData.questionnaireId}
              onChange={(e) => setFormData({ ...formData, questionnaireId: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., imported-template-2026"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Importing...' : 'Import'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteTemplateVersionsModal: React.FC<{
  template: QuestionnaireVersion;
  onClose: () => void;
  onDeleteSelected: (versions: number[]) => void;
  isLoading: boolean;
}> = ({ template, onClose, onDeleteSelected, isLoading }) => {
  const versionsSorted = useMemo(
    () => [...template.versions].sort((a, b) => b.version - a.version),
    [template.versions]
  );
  const maxVersion = useMemo(
    () => Math.max(0, ...template.versions.map((v) => v.version)),
    [template.versions]
  );
  const [selected, setSelected] = useState<Set<number>>(new Set());

  useEffect(() => {
    setSelected(new Set([maxVersion]));
  }, [template, maxVersion]);

  const toggle = (version: number, disabled: boolean) => {
    if (disabled) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(version)) {
        next.delete(version);
      } else {
        next.add(version);
      }
      return next;
    });
  };

  const handleDelete = () => {
    const chosen = [...selected].sort((a, b) => a - b);
    if (chosen.length === 0) {
      toast.error('Select at least one version to delete');
      return;
    }
    onDeleteSelected(chosen);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-gray-900 mb-1">Delete template versions</h2>
        <p className="text-sm text-gray-600 mb-4 font-mono">{template.questionnaireId}</p>
        <p className="text-sm text-gray-600 mb-4">
          Choose one or more versions to remove. The highest version is selected by default. You cannot delete the active
          version or any version that still has evaluations.
        </p>
        <ul className="space-y-2 mb-6">
          {versionsSorted.map((v) => {
            const blocked =
              v.isActive || (v.assessmentCount ?? 0) > 0;
            return (
              <li
                key={v.version}
                className={`flex items-start gap-3 border rounded-lg p-3 ${blocked ? 'bg-gray-50 opacity-80' : ''}`}
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selected.has(v.version)}
                  disabled={blocked || isLoading}
                  onChange={() => toggle(v.version, blocked)}
                  title={
                    v.isActive
                      ? 'Deactivate another version before deleting this one'
                      : (v.assessmentCount ?? 0) > 0
                        ? 'Cannot delete a version referenced by evaluations'
                        : 'Select to delete'
                  }
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">
                    Version {v.version}
                    {v.isActive ? (
                      <span className="ml-2 text-xs font-normal text-amber-800">active</span>
                    ) : null}
                  </div>
                  <div className="text-sm text-gray-700 truncate">{v.name}</div>
                  <div className="text-xs text-gray-500">
                    {v.questionCount ?? 0} questions · {v.assessmentCount ?? 0} evaluations
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={isLoading}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="btn bg-red-600 text-white hover:bg-red-700 border-red-600"
            disabled={isLoading}
          >
            {isLoading ? 'Deleting...' : 'Delete selected'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Duplicate Questionnaire Modal Component
const DuplicateQuestionnaireModal: React.FC<{
  questionnaireId: number;
  onClose: () => void;
  onDuplicate: (data: { questionnaireId: number; newQuestionnaireId: string; name: string; description?: string }) => void;
  isLoading: boolean;
}> = ({ questionnaireId, onClose, onDuplicate, isLoading }) => {
  const [formData, setFormData] = useState({
    newQuestionnaireId: '',
    name: '',
    description: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.newQuestionnaireId || !formData.name) {
      toast.error('Questionnaire ID and name are required');
      return;
    }
    onDuplicate({
      questionnaireId,
      newQuestionnaireId: formData.newQuestionnaireId,
      name: formData.name,
      description: formData.description || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Duplicate Questionnaire</h2>
        <p className="text-sm text-gray-600 mb-4">
          Create a copy of <strong>{questionnaireId}</strong>
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Questionnaire ID *
            </label>
            <input
              type="text"
              value={formData.newQuestionnaireId}
              onChange={(e) => setFormData({ ...formData, newQuestionnaireId: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="e.g., default-v2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Duplicating...' : 'Duplicate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Version Modal Component
const VersionModal: React.FC<{
  questionnaireId: number;
  onClose: () => void;
  onActivate: (data: { questionnaireId: number; version: number }) => void;
  isLoading: boolean;
}> = ({ questionnaireId, onClose, onActivate, isLoading }) => {
  const queryClient = useQueryClient();
  const { data: versions, refetch } = useQuery(
    ['questionnaire-versions', questionnaireId],
    () => questionnaireService.getQuestionnaireVersions(questionnaireId, false), // Use numeric ID, not label
    {
      enabled: !!questionnaireId,
    }
  );

  const deleteVersionMutation = useMutation(
    (version: number) => questionnaireService.deleteVersion(questionnaireId, version, false), // Use numeric ID, not label
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['questionnaire-versions', questionnaireId], { refetchActive: true, refetchInactive: true });
        queryClient.invalidateQueries(['questionnaires'], { refetchActive: true, refetchInactive: true });
        toast.success('Version deleted successfully');
        refetch();
      },
      onError: (err: any) => {
        toast.error(err.message || 'Failed to delete version');
      },
    }
  );

  const handleActivate = (version: number) => {
    onActivate({ questionnaireId, version });
  };

  const handleDelete = (version: number, assessmentCount: number) => {
    if (assessmentCount > 0) {
      toast.error(`Cannot delete version ${version}: ${assessmentCount} assessment(s) are using this version`);
      return;
    }
    if (window.confirm(`Are you sure you want to delete version ${version}? This action cannot be undone.`)) {
      deleteVersionMutation.mutate(version);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Version History</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ×
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">Questionnaire: <strong>{questionnaireId}</strong></p>
        {versions && versions.length > 0 ? (
          <div className="space-y-2">
            {versions.map((v) => (
              <div
                key={v.version}
                className={`border rounded-lg p-4 ${v.isActive ? 'border-green-500 bg-green-50' : 'border-gray-200'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Version {v.version}</span>
                      {v.isActive && (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-900">{v.name}</p>
                    <p className="text-xs text-gray-500">
                      Created by {v.createdBy.firstName} {v.createdBy.lastName} on{' '}
                      {new Date(v.createdAt).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-gray-500">
                      {v.questionCount || 0} questions • {v.assessmentCount || 0} assessments
                    </p>
                    {/* Impact Analysis - Show assessments using this version */}
                    {v.assessments && v.assessments.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-1">
                          Assessments using this version:
                        </p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {v.assessments.map((assessment: any) => (
                            <div key={assessment.id} className="text-xs text-gray-600 flex items-center gap-2">
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {assessment.status}
                              </span>
                              <span className="font-medium">{assessment.clientName || `Client ${assessment.clientId}`}</span>
                              <span className="text-gray-400">•</span>
                              <span className="text-gray-500">
                                {new Date(assessment.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {v.assessments && v.assessments.length > 0 && !v.isActive && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                        ⚠️ This version has {v.assessments.length} active assessment{v.assessments.length !== 1 ? 's' : ''}. Activating a different version may affect these assessments.
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!v.isActive && (
                      <button
                        onClick={() => {
                          if (v.assessments && v.assessments.length > 0) {
                            const confirmed = window.confirm(
                              `This version has ${v.assessments.length} assessment(s). Activating this version will make it the active version. Continue?`
                            );
                            if (confirmed) {
                              handleActivate(v.version);
                            }
                          } else {
                            handleActivate(v.version);
                          }
                        }}
                        className="btn btn-secondary text-sm"
                        disabled={isLoading}
                      >
                        Activate
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(v.version, v.assessmentCount || 0)}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isLoading || deleteVersionMutation.isLoading || (v.assessmentCount || 0) > 0 || v.isActive}
                      title={
                        v.isActive
                          ? 'Cannot delete active version'
                          : (v.assessmentCount || 0) > 0
                            ? `Cannot delete: ${v.assessmentCount} assessment(s) using this version`
                            : 'Delete this version'
                      }
                    >
                      {deleteVersionMutation.isLoading ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No versions found.</p>
        )}
        <div className="mt-4 flex justify-end">
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireEditor;

